import logger from "@utils/logger";

function getStorageItem<T = string>(key: string, defaultValue: T, parser?: (value: string) => T, parseKey = "[]"): T {
  try {
    if (typeof parser === "function") {
      return parser(localStorage.getItem(key) ?? parseKey);
    }
    return (localStorage.getItem(key) as unknown as T) ?? defaultValue;
  } catch (error) {
    logger._console.error(error);
    return defaultValue;
  }
}

const sessionFallback = new Map<string, string>();
let sessionUsable: boolean | null = null;

const ensureSessionAvailable = (): boolean => {
  if (sessionUsable !== null) return sessionUsable;
  try {
    const probeKey = "__session_probe__";
    window.sessionStorage.setItem(probeKey, "1");
    window.sessionStorage.removeItem(probeKey);
    sessionUsable = true;
  } catch (error) {
    sessionUsable = false;
    logger._console.warn("sessionStorage unavailable, using in-memory fallback", error);
  }
  return sessionUsable;
};

const safeSessionGetItem = (key: string): string | null => {
  if (ensureSessionAvailable()) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      sessionUsable = false;
      logger._console.error(error);
    }
  }
  return sessionFallback.get(key) ?? null;
};

const safeSessionSetItem = (key: string, value: string): void => {
  if (ensureSessionAvailable()) {
    try {
      window.sessionStorage.setItem(key, value);
      sessionFallback.set(key, value);
      return;
    } catch (error) {
      sessionUsable = false;
      logger._console.error(error);
    }
  }
  sessionFallback.set(key, value);
};

const safeSessionRemoveItem = (key: string): void => {
  if (ensureSessionAvailable()) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      sessionUsable = false;
      logger._console.error(error);
    }
  }
  sessionFallback.delete(key);
};

function removeFromStorage(storageKey: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    logger._console.error(error);
  }
}

function safeParseJSON<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    logger._console.error(error);
    return fallback;
  }
}

function readJSONFromStorage<T>(storageKey: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  return safeParseJSON<T>(localStorage.getItem(storageKey), fallback);
}

function writeJSONToStorage<T>(storageKey: string, value: T): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    logger._console.error(error);
  }
}

export {
  getStorageItem,
  readJSONFromStorage,
  removeFromStorage,
  safeParseJSON,
  safeSessionGetItem,
  safeSessionRemoveItem,
  safeSessionSetItem,
  writeJSONToStorage,
};
